import { createSelector } from "reselect";

const EMPTY_ARRAY = [];

// Step 1: Base selector to get jobDetails from state
const selectJobDetailsBase = (state) => state.jobDetails.jobDetails;

// Export the base selector for components that need the full jobDetails state
export const selectJobDetails = selectJobDetailsBase;

export const selectJobDetailsSection = createSelector(
  [selectJobDetailsBase, (_, sectionName) => sectionName],
  (jobDetails, sectionName) => jobDetails[sectionName] || []
);
export const selectGeneralProject = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.generalProject || EMPTY_ARRAY
);
export const selectGeneralStation = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.generalStation || []
);
export const selectSoldToShipTo = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.soldToShipTo || []
);
export const selectPriceBreakdown = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.priceBreakdown || []
);
export const selectOwnersManual = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.ownersManual || []
);
export const selectHcRequirements = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.hcRequirements || []
);
export const selectSkidConstrution = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.skidConstruction || []
);
export const selectPumpManifoldPiping = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.pumpManifoldPiping || []
);
export const selectStationEnclosure = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.stationEnclosure || []
);
export const selectDropPipeComments = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.dropPipeComments || []
);
export const selectDropPipes = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.dropPipes || []
);
export const selectLevelControls = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.levelControls || []
);
export const selectFillSources = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.fillSources || []
);
export const selectPumps = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.pumps || []
);
export const selectPumpDetails = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.pumpDetails || []
);
export const selectSchedulePumpDetails = createSelector(
  [(state) => state.pumpDetails],
  (pumpDetails) => pumpDetails?.data || []
);
export const selectWaterTreatment = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.waterTreatment || []
);
export const selectInletScreen = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.inletScreen || []
);
export const selectInletStrainer = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.inletStrainer || []
);
export const selectDischargeStrainer = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.dischargeStrainer || []
);
export const selectDischargeFilter = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.dischargeFilter || []
);
export const selectRo = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.roSystem || []
);
export const selectUv = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.uvSystem || []
);
export const selectFertigation = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.fertigation || []
);
export const selectShipLoose = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.shipLoose || []
);
export const selectSkyHarvester = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.skyHarvester || []
);
export const selectUnregulatedPower = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.unregulatedPower || []
);
export const selectUnregulatedBreakers = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.unregulatedBreakers || []
);
export const selectRegulatedPower = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.regulatedPower || []
);
export const selectRegulatedBreakers = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.regulatedBreakers || []
);
export const selectSequenceOfOperations = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.sequenceOfOperations || []
);
export const selectElectricalOptions = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.electricalOptions || []
);
export const selectTelemetry = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.telemetry || []
);
export const selectTelemetryAccessories = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.telemetryAccessories || []
);
export const selectSelectedPumpOptionList = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.selectedPumpOptionList || []
);
export const selectCustomOptions = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.customOptions || []
);
export const selectWarningReport = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.warningReport || []
);
export const selectChangedOptions = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.changedOptions || []
);
export const selectChangeOrderParent = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.changeOrderParent || []
);

// Selector for job-specific change order details (used in JobDetailPage)
export const selectJobChangeOrderDetails = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.changeOrderDetails || []
);

// Selector for global change order details (used in JobSchedule)
export const selectChangeOrderDetails = (state) => state.changeOrderDetailsGlobal?.data || EMPTY_ARRAY;
export const selectControlLogicOnly = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.controlLogicOnly || []
);
export const selectShipEarlyParent = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.shipEarlyParent || EMPTY_ARRAY
);
export const selectShipEarlyItems = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.shipEarlyItems || EMPTY_ARRAY
);
export const selectEngineeringTasks = createSelector(
  [selectJobDetailsBase],
  (jobDetails) => jobDetails?.engineeringTasks || []
);
export const selectJobId = (state) => state.jobId.jobId;

export const selectChangeOrderMailingList = (state) => state.changeOrder.changeOrderMailList;

export const selectHoverDescriptions = (state) => state.hoverDescriptions.hoverDescriptions;
export const selectGlobalVariables = (state) => state.globalVariables.globalVariables;

export const selectFieldDescriptions = (state) => state.fieldDescriptions.data;

export const selectActiveFiltration = (state, jobId) => state.activeFiltration.activeFiltration[jobId];

// export const selectChangeOrderMailList = (state) =>
//   state.changeOrder.changeOrderMailList || [];
